import React, { FC, useLayoutEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { NetworkStatus } from '@apollo/client';
import { range, throttle } from 'lodash';
import { idify } from '@src/util/idify';
import {
  OpeningSearchTagNameEnum,
  OpeningSearchTagType,
  OpeningsQuery,
  OpeningStateEnum,
  RecommendedUsersSortOptionEnum,
  useOpeningsCountQuery,
  useOpeningsQuery,
  useTeamMembersQueryQuery,
} from '@src/graphql/generated';
import { Recruiters } from '@src/containers/Source/Recruiters';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { QueryParams } from '@src/containers/Source/interfaces/queryParams.interface';
import { SelectedRecruiter } from '@src/containers/Source/enums/selectedRecruiter.enum';
import { TabInterface } from '@src/containers/Source/OpeningStates/interfaces/tab.interface';
import { FILTERS } from '@src/containers/Source/OpeningStates';
import { OpeningsEmptyState } from '@src/containers/Source/OpeningsEmptyState';
import { Opening } from './Opening';
import { OpeningSkeleton } from './Skeleton';
import './styles.scss';

export const OpeningsSection: FC = () => {
  const [selectedFilter, setSelectedFilter] = useState<TabInterface>(FILTERS[OpeningStateEnum.ACTIVE]);

  const { queryParams, updateQueryParams } = useQueryParams<QueryParams>();

  const { data: teamMembersData, loading: isTeamMembersDataLoading } = useTeamMembersQueryQuery();

  const activeRecruiter = queryParams?.activeRecruiter || SelectedRecruiter.ALL_RECRUITERS_OPTION;

  const recruiterSearchTags: OpeningSearchTagType[] = useMemo(() => {
    return activeRecruiter === SelectedRecruiter.ALL_RECRUITERS_OPTION
      ? []
      : activeRecruiter === SelectedRecruiter.SCRAPED_OPENINGS_OPTION
      ? [{ tag: OpeningSearchTagNameEnum.SOURCE_TYPE, value: 'SCRAPER' }]
      : [{ tag: OpeningSearchTagNameEnum.RECRUITER, value: activeRecruiter }];
  }, [activeRecruiter]);

  const {
    data: activeOpeningsCountData,
    loading: isActiveOpeningsLoading,
    updateQuery: updateActiveOpeningsCountQuery,
  } = useOpeningsCountQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      searchTags: [...recruiterSearchTags, ...FILTERS[OpeningStateEnum.ACTIVE].searchTags],
    },
  });

  const {
    data: draftOpeningsCountData,
    loading: isDraftOpeningsLoading,
    updateQuery: updateDraftOpeningsCountQuery,
  } = useOpeningsCountQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      searchTags: [...recruiterSearchTags, ...FILTERS[OpeningStateEnum.DRAFT].searchTags],
    },
  });

  const {
    data: inactiveOpeningsCountData,
    loading: isInactiveOpeningsLoading,
    updateQuery: updateInactiveOpeningsCountQuery,
  } = useOpeningsCountQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      searchTags: [...recruiterSearchTags, ...FILTERS[OpeningStateEnum.INACTIVE].searchTags],
    },
  });

  const {
    data: openingsData,
    fetchMore: fetchMoreOpenings,
    networkStatus,
  } = useOpeningsQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      paginationOptions: { limit: 10 },
      recommendedUsersPagination: {
        limit: 5,
        sortBy: RecommendedUsersSortOptionEnum.RELEVANCE,
      },
      searchTags: [...recruiterSearchTags, ...selectedFilter.searchTags],
    },
  });

  const teamMembers = teamMembersData?.company.teamMembers || [];

  const onSelectRecruiter = (recruiterId: string): void => {
    updateQueryParams({
      activeRecruiter: recruiterId,
    });
  };

  const isFetchMoreLoading = networkStatus === NetworkStatus.fetchMore;
  const isInitialFetchLoading = [NetworkStatus.setVariables, NetworkStatus.loading].includes(networkStatus);
  const isOpeningsCountLoading = isInactiveOpeningsLoading || isActiveOpeningsLoading || isDraftOpeningsLoading;
  const isOpeningsDataLoading = isFetchMoreLoading || isInitialFetchLoading;
  const openings = openingsData?.openings.result || [];
  const pagination = openingsData?.openings.pagination;

  const labels = [
    {
      count: activeOpeningsCountData?.openings.pagination.totalCount,
      label: 'Published',
      value: OpeningStateEnum.ACTIVE,
    },
    {
      count: draftOpeningsCountData?.openings.pagination.totalCount,
      label: 'Draft',
      value: OpeningStateEnum.DRAFT,
    },
    {
      count: inactiveOpeningsCountData?.openings.pagination.totalCount,
      label: 'Inactive',
      value: OpeningStateEnum.INACTIVE,
    },
  ];

  const isOpeningStateVisibleInFilter = (
    filter: OpeningStateEnum.ACTIVE | OpeningStateEnum.DRAFT | OpeningStateEnum.INACTIVE,
    state: OpeningStateEnum,
  ) => {
    return FILTERS[filter].searchTags.some(({ value }) => {
      return state === value;
    });
  };

  const onUpdateOpening = (updatedOpening, _action) => {
    const previousOpening = openings.find(opening => idify(opening) === idify(updatedOpening));

    if (previousOpening) {
      const filterToUpdateCacheFunctionMapping = {
        ACTIVE: updateActiveOpeningsCountQuery,
        DRAFT: updateDraftOpeningsCountQuery,
        INACTIVE: updateInactiveOpeningsCountQuery,
      };

      for (const [filter, updateCacheFunction] of Object.entries(filterToUpdateCacheFunctionMapping)) {
        // If the opening wasn't counted in the filter previously but matches
        // the search tags of the filter now we increase the count. In the case
        // of the all filter we only allow decreasing the count but it can't be
        // increased.
        if (
          !isOpeningStateVisibleInFilter(filter, previousOpening.state) &&
          isOpeningStateVisibleInFilter(filter, updatedOpening.state)
        ) {
          updateCacheFunction(prev => ({
            ...prev,
            openings: {
              ...prev.openings,
              pagination: {
                ...prev.openings.pagination,
                totalCount: prev.openings.pagination.totalCount + 1,
              },
            },
          }));
        }

        // If the opening was counted in the filter currently but should not be
        // after the state updated we decrease the count.
        if (
          isOpeningStateVisibleInFilter(filter, previousOpening.state) &&
          !isOpeningStateVisibleInFilter(filter, updatedOpening.state)
        ) {
          updateCacheFunction(prev => ({
            ...prev,
            openings: {
              ...prev.openings,
              pagination: {
                ...prev.openings.pagination,
                totalCount: Math.max(prev.openings.pagination.totalCount - 1, 0),
              },
            },
          }));
        }
      }
    }
  };

  useLayoutEffect(() => {
    const onWindowScroll = throttle(() => {
      const scrollHeight = document.body.scrollHeight;

      const { scrollY, innerHeight } = window;

      const distanceFromBottom = scrollHeight - scrollY - innerHeight;
      const hasPagination = pagination && pagination.hasMoreResults && pagination.maxCursor;

      if (!isOpeningsDataLoading && hasPagination && distanceFromBottom <= 200) {
        fetchMoreOpenings({
          variables: {
            paginationOptions: {
              limit: 10,
              minCursor: pagination.maxCursor,
            },
            searchTags: [...recruiterSearchTags, ...selectedFilter.searchTags],
          },
        });
      }
    }, 250);

    document.addEventListener('scroll', onWindowScroll);

    return () => {
      document.removeEventListener('scroll', onWindowScroll);

      onWindowScroll.cancel();
    };
  }, [recruiterSearchTags, selectedFilter, pagination, isOpeningsDataLoading]);

  return (
    <div className="start-headhunt-openings">
      <div className="start-headhunt-openings__header">
        <div className="start-headhunt-openings__header-title">Your jobs</div>

        <div className="start-headhunt-openings__select">
          <div className="start-headhunt-openings__select-recruiter">
            <Recruiters
              isLoading={isTeamMembersDataLoading}
              onSelectRecruiter={onSelectRecruiter}
              recruiters={teamMembers}
              selectedRecruiterId={activeRecruiter}
            />
          </div>

          <div className="start-headhunt-openings__select-divider" />

          <div className="start-headhunt-openings__labels">
            {labels.map(labelItem => {
              return (
                <div
                  className={classnames(
                    'start-headhunt-openings__label',
                    labelItem.value === selectedFilter.key && 'start-headhunt-openings__label--active',
                  )}
                  key={labelItem.label}
                  onClick={() => setSelectedFilter(FILTERS[labelItem.value])}
                >
                  {labelItem.label} {!isOpeningsCountLoading && `(${labelItem.count})`}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="start-headhunt-openings__list">
        {!isInitialFetchLoading &&
          openings.map((opening: OpeningsQuery['openings']['result'][0]) => {
            if (!opening.title) {
              return null;
            }

            return <Opening key={opening.id} onUpdateOpening={onUpdateOpening} opening={opening} />;
          })}

        {(isInitialFetchLoading || isFetchMoreLoading) &&
          range(3).map(item => {
            return <OpeningSkeleton key={item} />;
          })}

        {!isInitialFetchLoading && !openings.length && <OpeningsEmptyState />}
      </div>
    </div>
  );
};
